/**
 * @file look-space.ts
 * @description Числа группы «Отступы» секции v3 (phase19.16, партия SB120
 * «свобода выбора», этап 1 «секция-герой»; site-builder.md SB120, SB100;
 * проект docs/phases/phase19.16-materials/uiux-sb120-stage1.md §2.2, §5;
 * проект данных db-design-sb120-stage1.md §1).
 *
 * Чистые функции без React: сколько даёт «как у сайта» в px на ПК (правило
 * витрины section-visibility.ts / section.css), ступени-подсказки
 * 0/S/M/L/XL × плотность магазина (`spaceStepPx`), значение на телефоне
 * (своё или ПК × доля), связь «⛓ одинаково» слева/справа (не хранится:
 * связь = `innerLeft === innerRight`, включая оба отсутствуют).
 */

import {
  SECTION_LOOK_LIMITS,
  SITE_THEME_LIMITS,
  spaceStepPx,
  type SectionLook,
  type SectionSpace,
  type SectionSpaceSide,
  type SiteSpacingDensity,
  type SpaceStep,
} from "@chaiberi/shared";

import type { NumberPreset } from "../../components/site-fields/NumberPresets";
import type { SpaceHighlightPart } from "../site-view/canvas-messages";

/** Что нужно от темы: плотность и доля телефона. */
export interface SpaceTheme {
  density: SiteSpacingDensity;
  phoneFactor: number;
}

/** Тема по умолчанию, пока оформление сайта не пришло (uiux §2: ступени по «Обычно»). */
export const DEFAULT_SPACE_THEME: SpaceTheme = { density: SITE_THEME_LIMITS.density.default, phoneFactor: SECTION_LOOK_LIMITS.phoneFactor.default };

/** Боковые поля сетки «как у сайта» по ширине экрана (globals.css `.px-section`: 22 · 40 · 70). */
export const SITE_SIDE_PX = [22, 40, 70] as const;

/** Подписи шести полей отступов (порядок на экране). */
export const SPACE_SIDE_LABELS: Readonly<Record<SectionSpaceSide, string>> = {
  outerTop: "Снаружи сверху",
  outerBottom: "Снаружи снизу",
  innerTop: "Внутри сверху",
  innerBottom: "Внутри снизу",
  innerLeft: "Внутри слева",
  innerRight: "Внутри справа",
};

/** Подсветка на канве по полю (`space-highlight`); слева/справа — новая часть `inner-x`. */
export const SPACE_SIDE_HIGHLIGHT: Readonly<Record<SectionSpaceSide, SpaceHighlightPart>> = {
  outerTop: "outerTop",
  outerBottom: "outerBottom",
  innerTop: "inner",
  innerBottom: "inner",
  innerLeft: "inner-x",
  innerRight: "inner-x",
};

/** Боковые поля — до 120 px, остальные — до 200 px (look-limits.ts). */
export function sideRange(side: SectionSpaceSide): { min: number; max: number } {
  return side === "innerLeft" || side === "innerRight" ? SECTION_LOOK_LIMITS.side : SECTION_LOOK_LIMITS.space;
}

/** Ступень «как у сайта» сверху/снизу: снаружи L; внутри — M у подложки, 0 у прозрачной, L на своём фоне. */
function siteStep(side: SectionSpaceSide, look: Pick<SectionLook, "card" | "bg">): SpaceStep {
  if (side === "outerTop" || side === "outerBottom") return "l";
  if (look.card.on) return "m";
  return look.bg.kind === "transparent" ? "0" : "l";
}

/**
 * «Как у сайта» в px на ПК. Слева/справа: 0 при «Во всю», иначе поле сетки
 * на широком экране (70 × плотность; на узких 22 и 40 — в подписи); у
 * подложки — max(внутри, S) (section.css: отступ подложки по бокам не
 * меньше S), где «внутри» — отступ сверху блока.
 */
export function siteSpacePx(side: SectionSpaceSide, look: Pick<SectionLook, "card" | "bg" | "container"> & { space?: SectionSpace }, density: SiteSpacingDensity): number {
  if (side === "innerLeft" || side === "innerRight") {
    if (look.container === "full") return 0;
    if (look.card.on) return Math.max(look.space?.innerTop ?? spaceStepPx(siteStep("innerTop", look), density), spaceStepPx("s", density));
    return Math.round(SITE_SIDE_PX[2] * density * 10) / 10;
  }
  return spaceStepPx(siteStep(side, look), density);
}

/**
 * Подпись «как у сайта» у боковых: «как у сайта: 22 · 40 · 70 по ширине
 * экрана»; у подложки — «как у сайта: max(внутри, S) = N px».
 */
export function siteSideHint(density: SiteSpacingDensity, look?: Pick<SectionLook, "card" | "bg" | "container"> & { space?: SectionSpace }): string {
  if (look?.card.on && look.container !== "full") return `как у сайта на подложке: max(внутри, S) = ${formatPx(siteSpacePx("innerLeft", look, density))} px`;
  const k = density;
  return `как у сайта: ${SITE_SIDE_PX.map((v) => formatPx(v * k)).join(" · ")} по ширине экрана`;
}

/** «16», «30,7» — px с запятой, до десятых. */
export function formatPx(value: number): string {
  return String(Math.round(value * 10) / 10).replace(".", ",");
}

/** Ступени-подсказки шкалы сайта при плотности магазина: [0] [S 16] [M 32] [L 64] [XL 96]. */
export function spacePresets(density: SiteSpacingDensity, steps: readonly SpaceStep[] = ["0", "s", "m", "l", "xl"]): NumberPreset[] {
  return steps.map((step) => {
    const value = spaceStepPx(step, density);
    return { label: step === "0" ? "0" : `${step.toUpperCase()} ${formatPx(value)}`, value };
  });
}

/** Доля телефона секции: своя или темы. */
export function phoneFactorOf(space: Pick<SectionSpace, "phoneFactor">, theme: SpaceTheme): number {
  return space.phoneFactor ?? theme.phoneFactor;
}

/** Отступ на ПК: своё число или «как у сайта». */
export function desktopSpacePx(side: SectionSpaceSide, look: Pick<SectionLook, "card" | "bg" | "container" | "space">, theme: SpaceTheme): number {
  return look.space[side] ?? siteSpacePx(side, look, theme.density);
}

/** На телефоне: своё число телефона (`space.phone`) или ПК × доля. */
export function phoneSpacePx(side: SectionSpaceSide, look: Pick<SectionLook, "card" | "bg" | "container" | "space">, theme: SpaceTheme): { value: number; own: boolean } {
  const own = look.space.phone?.[side];
  if (own !== undefined) return { value: own, own: true };
  return { value: desktopSpacePx(side, look, theme) * phoneFactorOf(look.space, theme), own: false };
}

/** Связаны ли слева и справа: равны (включая оба «как у сайта»). */
export function sidesLinked(space: Pick<SectionSpace, "innerLeft" | "innerRight">): boolean {
  return space.innerLeft === space.innerRight;
}

/** Записать поле отступа; `undefined` — снять ключ («как у сайта»). */
export function setSpaceSide(space: SectionSpace, side: SectionSpaceSide, value: number | undefined): SectionSpace {
  const next = { ...space };
  if (value === undefined) delete next[side];
  else next[side] = value;
  return next;
}

/** Своё число телефона; `undefined` снимает ключ, пустой `phone` убирается. */
export function setPhoneSide(space: SectionSpace, side: SectionSpaceSide, value: number | undefined): SectionSpace {
  const phone = { ...(space.phone ?? {}) };
  if (value === undefined) delete phone[side];
  else phone[side] = value;
  const next: SectionSpace = { ...space, phone };
  if (Object.keys(phone).length === 0) delete next.phone;
  return next;
}
